/* eslint-disable indent */
import { t } from 'i18next'

import { DEFAULT_NTB_LOOKBACK_MONTHS } from '~/modules/retailMedia/constants'

import { MetricTooltip } from '../components/MetricMethodologyTooltip'

export type NtbMetricKey =
  | 'newToBrandCustomers'
  | 'newToBrandCustomersPercent'
  | 'newToBrandRevenue'
  | 'newToBrandRevenuePercent'

export type NtbCardType =
  | 'new_to_brand_customers'
  | 'new_to_brand_customers_percent'
  | 'new_to_brand_revenue'
  | 'new_to_brand_revenue_percent'

export type NtbMetric = {
  /** camelCase: coluna das tabelas, `formatted` dos DTOs, card da view de campanha */
  key: NtbMetricKey
  /** snake_case: campo da API, sortKey e tipo do card no dashboard */
  rawKey: NtbCardType
  /** métrica-base que a API devolve; o percentual acompanha a dela */
  availabilityKey: 'newToBrandCustomers' | 'newToBrandRevenue'
  format: 'unit' | 'currency' | 'percent'
  /** card de percentual mostra o rótulo do absoluto: o ícone já é um "%" */
  cardLabelOf?: NtbMetricKey
}

/**
 * Descritor único das 4 métricas de NTB. Cada superfície (cards do dashboard,
 * cards da view de campanha, colunas das tabelas) deriva daqui rótulo,
 * tooltip, ordenação e disponibilidade, em vez de repetir os 4 blocos.
 */
export const NTB_METRICS: readonly NtbMetric[] = [
  {
    key: 'newToBrandCustomers',
    rawKey: 'new_to_brand_customers',
    availabilityKey: 'newToBrandCustomers',
    format: 'unit'
  },
  {
    key: 'newToBrandCustomersPercent',
    rawKey: 'new_to_brand_customers_percent',
    availabilityKey: 'newToBrandCustomers',
    format: 'percent',
    cardLabelOf: 'newToBrandCustomers'
  },
  {
    key: 'newToBrandRevenue',
    rawKey: 'new_to_brand_revenue',
    availabilityKey: 'newToBrandRevenue',
    format: 'currency'
  },
  {
    key: 'newToBrandRevenuePercent',
    rawKey: 'new_to_brand_revenue_percent',
    availabilityKey: 'newToBrandRevenue',
    format: 'percent',
    cardLabelOf: 'newToBrandRevenue'
  }
]

// chaves de coluna NTB nos schemas das tabelas e tipos de card na view de campanha
export const NTB_COLUMN_KEYS: NtbMetricKey[] = NTB_METRICS.map(
  metric => metric.key
)

// tipos de card NTB no dashboard (snake_case: `type` é a chave persistida do CardSelector)
export const NTB_CARD_TYPES = new Set<string>(
  NTB_METRICS.map(metric => metric.rawKey)
)

export const availableNtbMetrics = (
  availableMetrics?: Partial<NewMetricsAvailability> | null
): NtbMetric[] =>
  NTB_METRICS.filter(metric => !!availableMetrics?.[metric.availabilityKey])

export type NtbTooltipValues = {
  months: number
  checkpointStart: string
  checkpointEnd: string
}

/** Valores de interpolação dos tooltips de NTB; sem janela, traço. */
export const buildNtbTooltipValues = ({
  lookbackMonths = DEFAULT_NTB_LOOKBACK_MONTHS,
  ordersWindow
}: {
  lookbackMonths?: number
  ordersWindow?: NtbOrdersWindow
} = {}): NtbTooltipValues => ({
  months: lookbackMonths,
  checkpointStart: ordersWindow?.checkpointStart ?? '-',
  checkpointEnd: ordersWindow?.checkpointEnd ?? '-'
})

// Range da janela NTB usado como badge dos cards, igual ao dos tooltips
export const getNtbBadgeLabel = (
  ordersWindow: NtbOrdersWindow
): string | undefined =>
  ordersWindow
    ? `${ordersWindow.checkpointStart} - ${ordersWindow.checkpointEnd}`
    : undefined

// chaves dinâmicas: o `t` tipado não aceita template string
export const translateNtbKey = t as unknown as (key: string) => string
const translate = translateNtbKey

export const ntbMetricLabel = (metric: NtbMetric) =>
  translate(`rm:metrics.${metric.key}`)

type BuildNtbColumnsProps<Row> = {
  availableMetrics?: NewMetricsAvailability
  ntbValues: NtbTooltipValues
  /** `ad`: tooltip do grão de anúncio, que avisa que a soma das linhas não fecha */
  variant?: 'table' | 'ad'
  /** false para tabelas sem ordenação pela API (histórico diário) */
  sortable?: boolean
  render: (metric: NtbMetric) => TableSchemaItem<Row>['render']
}

/** As 4 colunas de NTB de um schema, só as disponíveis, na ordem do descritor. */
export const buildNtbColumns = <Row,>({
  availableMetrics,
  ntbValues,
  variant = 'table',
  sortable = true,
  render
}: BuildNtbColumnsProps<Row>): TableSchema<Row> =>
  Object.fromEntries(
    availableNtbMetrics(availableMetrics).map(metric => [
      metric.key,
      {
        title: ntbMetricLabel(metric),
        helpTitle: translate(`rm:metrics.${metric.key}.tooltipTitle`),
        helpContent: (
          <MetricTooltip
            metric={variant === 'ad' ? `${metric.key}Ad` : metric.key}
            values={ntbValues}
          />
        ),
        ...(sortable
          ? { sortKey: metric.rawKey, sortInitialToggleDirection: 'DESC' }
          : {}),
        render: render(metric)
      }
    ])
  )

/** Só as colunas NTB que um schema já montou, para as estratégias não repetirem os 4 spreads. */
export const pickNtbColumns = <Row,>(
  columns: TableSchema<Row>
): TableSchema<Row> =>
  Object.fromEntries(
    NTB_COLUMN_KEYS.filter(key => key in columns).map(key => [
      key,
      columns[key]
    ])
  )
